import { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, ShieldCheck, X, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { EPCR } from '@/types';
import { checkCompleteness, type ElementCheck } from '@/lib/nemsis-completeness';
import { RuleTag } from '@/components/RuleTag';

/**
 * PostValidationModal — EMSPCR Foundation v1.0 §9.2.
 *
 * Three-panel Post-time review:
 *
 *   1. Mandatory check — any missing Mandatory elements block Post
 *      with a clickable checklist that jumps to the relevant tab.
 *
 *   2. Required check — missing Required elements are flagged but
 *      allow Post with a documented NOT-value (crew can mark
 *      "Not Recorded" / "Not Applicable" without leaving the modal).
 *
 *   3. Compliance summary — NEMSIS v3.5.1 element coverage, active
 *      conditional sets, RIDOH state-fields + trauma-registry +
 *      CMS medical-necessity signals. Shown before the final
 *      "Confirm Post" tap.
 *
 * This is a read-only preview — the modal does not itself submit to
 * RIEMSIS. Calling `onConfirmPost` hands control back to the ePCR
 * finalize workflow.
 */

interface Props {
  epcr: EPCR;
  onClose: () => void;
  onUpdate: (patch: Partial<EPCR>) => void;
  onConfirmPost: () => void;
  onJumpToTab?: (key: string) => void;
}

export function PostValidationModal({
  epcr, onClose, onUpdate, onConfirmPost, onJumpToTab,
}: Props) {
  const summary = useMemo(() => checkCompleteness(epcr), [epcr]);

  const canPost = summary.passesPost;

  function markNotValue(id: string) {
    // Minimal inline NOT-value application; for anything more complex
    // the crew taps "Fill in" and we route them to the relevant tab.
    import('@/lib/nemsis-completeness').then(({ notValuePatch }) => {
      const patch = notValuePatch(id, 'Not Recorded');
      if (Object.keys(patch).length > 0) onUpdate(patch);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col border border-slate-700 bg-slate-950 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className={cn(canPost ? 'text-emerald-400' : 'text-amber-400')} />
            <span className="text-sm font-bold uppercase tracking-widest text-slate-100">
              Post Validation
            </span>
            <span
              className={cn(
                'status-pill ml-1',
                canPost
                  ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                  : 'bg-amber-950 text-amber-300 border border-amber-800',
              )}
            >
              {canPost ? 'Passes Mandatory Check' : 'Mandatory Fields Missing'}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-200" title="Close">
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* 1. Mandatory */}
          <MandatorySection
            missing={summary.missingMandatory}
            onJump={onJumpToTab}
          />

          {/* 2. Required */}
          <RequiredSection
            missing={summary.missingRequired}
            onJump={onJumpToTab}
            onMarkNotValue={markNotValue}
          />

          {/* 3. Compliance Summary */}
          <ComplianceSummary epcr={epcr} summary={summary} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-700 bg-slate-900 px-4 py-2.5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            {summary.evaluatedElementIds.length} NEMSIS elements evaluated · {summary.activeSets.length} active set(s)
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-secondary gap-1 text-sm">
              Keep Editing
            </button>
            <button
              onClick={onConfirmPost}
              disabled={!canPost}
              className={cn(
                'btn-primary gap-1 text-sm',
                !canPost && 'opacity-40 cursor-not-allowed',
              )}
              title={canPost
                ? 'Submit this ePCR to RIEMSIS.'
                : 'Fix Mandatory fields before posting.'}
            >
              <CheckCircle2 size={14} /> Confirm Post to RIEMSIS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
function MandatorySection({
  missing, onJump,
}: { missing: ElementCheck[]; onJump?: (k: string) => void }) {
  if (missing.length === 0) {
    return (
      <section className="border border-emerald-800 bg-emerald-950/30 p-3">
        <div className="flex items-center gap-2 text-sm text-emerald-200">
          <CheckCircle2 size={14} />
          <span className="font-bold uppercase tracking-widest text-[11px]">Mandatory fields complete</span>
        </div>
      </section>
    );
  }
  return (
    <section className="border border-red-800 bg-red-950/30">
      <header className="flex items-center gap-2 border-b border-red-900 bg-red-950 px-3 py-1.5">
        <AlertTriangle size={14} className="text-red-300" />
        <span className="text-[11px] font-bold uppercase tracking-widest text-red-200">
          Cannot Post — {missing.length} Mandatory field{missing.length === 1 ? '' : 's'} missing
        </span>
      </header>
      <ul className="divide-y divide-red-950">
        {missing.map((c) => (
          <li key={c.element.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-slate-100 font-medium">{c.label}</div>
              <div className="text-[11px] font-mono text-slate-500">{c.element.id} · {c.usage}</div>
            </div>
            <RuleTag elementId={c.element.id} compact />
            <button
              onClick={() => onJump?.(tabKeyFor(c.element.id))}
              className="btn-secondary text-xs gap-1"
            >
              Fix <ChevronRight size={12} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RequiredSection({
  missing, onJump, onMarkNotValue,
}: {
  missing: ElementCheck[];
  onJump?: (k: string) => void;
  onMarkNotValue: (id: string) => void;
}) {
  if (missing.length === 0) return null;
  return (
    <section className="border border-amber-800 bg-amber-950/20">
      <header className="flex items-center gap-2 border-b border-amber-900 bg-amber-950 px-3 py-1.5">
        <AlertTriangle size={14} className="text-amber-300" />
        <span className="text-[11px] font-bold uppercase tracking-widest text-amber-200">
          {missing.length} Required field{missing.length === 1 ? '' : 's'} — NOT-value allowed
        </span>
      </header>
      <ul className="divide-y divide-amber-950/50">
        {missing.map((c) => (
          <li key={c.element.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-slate-100 font-medium">{c.label}</div>
              <div className="text-[11px] font-mono text-slate-500">
                {c.element.id} · {c.usage}
                {c.requiredBy && (
                  <span className="ml-1 text-amber-400/80">
                    (required by {c.requiredBy.name})
                  </span>
                )}
              </div>
            </div>
            <RuleTag elementId={c.element.id} compact />
            <button
              onClick={() => onMarkNotValue(c.element.id)}
              className="btn-secondary text-xs"
              title="Document that this field is not recorded / not applicable"
            >
              Mark NOT-Value
            </button>
            <button
              onClick={() => onJump?.(tabKeyFor(c.element.id))}
              className="btn-secondary text-xs gap-1"
            >
              Fill in <ChevronRight size={12} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ComplianceSummary({
  epcr, summary,
}: {
  epcr: EPCR;
  summary: ReturnType<typeof checkCompleteness>;
}) {
  const activeSetNames = summary.activeSets.filter((s) => s.id !== 'base').map((s) => s.name);
  const recommended = summary.missingRecommended.length;

  const rows: { label: string; state: 'ok' | 'warn' | 'info'; detail: string }[] = [
    {
      label: 'NEMSIS v3.5.1',
      state: summary.passesPost ? 'ok' : 'warn',
      detail: `${summary.evaluatedElementIds.length} element(s) evaluated${
        summary.passesPost
          ? ', all Mandatory complete'
          : `, ${summary.missingMandatory.length} Mandatory missing`
      }`,
    },
    {
      label: 'Conditional Sets',
      state: 'info',
      detail: activeSetNames.length > 0
        ? activeSetNames.join(' · ')
        : 'Base set only',
    },
    {
      label: 'RIDOH / RIEMSIS',
      state: epcr.toc_completed ? 'ok' : 'info',
      detail: epcr.toc_completed
        ? 'TOC timestamped · 2-hour post clock active'
        : 'Post clock starts at Transfer of Care',
    },
    {
      label: 'Trauma Registry',
      state: (epcr.trauma_triage_red?.length ?? 0) + (epcr.trauma_triage_yellow?.length ?? 0) > 0 ? 'ok' : 'info',
      detail: summary.activeSets.some((s) => s.id.startsWith('trauma'))
        ? `${(epcr.trauma_triage_red?.length ?? 0)} red · ${(epcr.trauma_triage_yellow?.length ?? 0)} yellow criteria`
        : 'Not applicable',
    },
    {
      label: 'RIDOH Opioid Surveillance',
      state: epcr.ridoh_opioid_surveillance ? 'info' : 'info',
      detail: epcr.ridoh_opioid_surveillance
        ? 'Flagged for CSTE opioid overdose dataset'
        : 'No surveillance triggers',
    },
    {
      label: 'Recommended Fields',
      state: recommended === 0 ? 'ok' : 'info',
      detail: recommended === 0
        ? 'All tracked Recommended fields populated'
        : `${recommended} optional gap(s) — no effect on Post`,
    },
  ];

  return (
    <section className="border border-slate-700 bg-slate-900/50">
      <header className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-3 py-1.5">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
          Post Summary
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          review before confirm
        </span>
      </header>
      <ul className="divide-y divide-slate-800">
        {rows.map((r) => (
          <li key={r.label} className="grid grid-cols-[200px_1fr_auto] gap-3 items-center px-3 py-2 text-sm">
            <span className="text-slate-400 text-xs font-mono uppercase tracking-widest">
              {r.label}
            </span>
            <span className="text-slate-200 text-xs">{r.detail}</span>
            <span className={cn(
              'text-[10px] font-bold uppercase tracking-widest',
              r.state === 'ok' && 'text-emerald-400',
              r.state === 'warn' && 'text-amber-400',
              r.state === 'info' && 'text-slate-500',
            )}>
              {r.state === 'ok' && '✓ Pass'}
              {r.state === 'warn' && '! Review'}
              {r.state === 'info' && 'Info'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function tabKeyFor(elementId: string): string {
  if (elementId.startsWith('ePatient'))     return 'patient';
  if (elementId.startsWith('eDispatch'))    return 'dispatch';
  if (elementId.startsWith('eSituation'))   return 'dispatch';
  if (elementId.startsWith('eInjury'))      return 'trauma';
  if (elementId.startsWith('eHistory'))     return 'history';
  if (elementId.startsWith('eVitals'))      return 'vitals';
  if (elementId.startsWith('eDisposition')) return 'disposition';
  if (elementId.startsWith('eTimes'))       return 'dispatch';
  return 'summary';
}
